/**
 * Wire-level types for the Kadanza Extension Page handshake.
 *
 * Hosts vary: UAT may omit `spaceId`, `pageId` and `designTokens`, and sends
 * `authToken.expires` as a number rather than a string. Everything is therefore
 * normalized through `parseHandshakeAck` / `parseTokenRefresh` before use, so
 * the rest of the app can rely on one shape.
 */

export const CONNECTION_EVENTS = {
  handshakeInit: 'HANDSHAKE_INIT',
  handshakeAck: 'HANDSHAKE_ACK',
  navigationChange: 'NAVIGATION_CHANGE',
  requestTokenRefresh: 'REQUEST_TOKEN_REFRESH',
  tokenRefresh: 'TOKEN_REFRESH',
} as const;

export type ConnectionEvent = (typeof CONNECTION_EVENTS)[keyof typeof CONNECTION_EVENTS];

export interface AuthToken {
  jwt: string;
  /** Unix timestamp in seconds, as a string. */
  expires: string;
}

export interface ExtensionDetails {
  extensionId: string;
  tenantId: string;
  tenantDomain: string;
  baseUrl: string;
  spaceId: string;
  pageId: string;
  locale: string;
}

export interface DesignTokens {
  primaryColor: string;
  fontFamily: string;
  /** One of `sm` | `md` | `lg` | `xl`. */
  borderRadius: string;
}

export interface HandshakeAckPayload {
  authToken: AuthToken;
  extensionDetails: ExtensionDetails;
  designTokens: DesignTokens;
}

export interface TokenRefreshPayload {
  authToken: AuthToken;
}

export const DEFAULT_DESIGN_TOKENS: DesignTokens = {
  primaryColor: '#5b6bcc',
  fontFamily: 'Source Sans 3',
  borderRadius: 'md',
};

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function normalizeExpires(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.floor(value));
  const asString = asNonEmptyString(value);
  if (asString && /^\d+$/.test(asString)) return asString;
  return null;
}

export function normalizeAuthToken(raw: unknown): AuthToken | null {
  if (!raw || typeof raw !== 'object') return null;
  const token = raw as { jwt?: unknown; expires?: unknown };
  const jwt = asNonEmptyString(token.jwt);
  if (!jwt) return null;
  const expires = normalizeExpires(token.expires) ?? String(Math.floor(Date.now() / 1000) + 3600);
  return { jwt, expires };
}

function normalizeDesignTokens(raw: unknown): DesignTokens {
  if (!raw || typeof raw !== 'object') return DEFAULT_DESIGN_TOKENS;
  const tokens = raw as Partial<DesignTokens>;
  return {
    primaryColor: asNonEmptyString(tokens.primaryColor) ?? DEFAULT_DESIGN_TOKENS.primaryColor,
    fontFamily: asNonEmptyString(tokens.fontFamily) ?? DEFAULT_DESIGN_TOKENS.fontFamily,
    borderRadius: asNonEmptyString(tokens.borderRadius) ?? DEFAULT_DESIGN_TOKENS.borderRadius,
  };
}

function normalizeExtensionDetails(raw: unknown): ExtensionDetails | null {
  if (!raw || typeof raw !== 'object') return null;
  const details = raw as Partial<ExtensionDetails>;
  const tenantId = asNonEmptyString(details.tenantId);
  if (!tenantId) return null;

  return {
    extensionId: asNonEmptyString(details.extensionId) ?? '',
    tenantId,
    tenantDomain: asNonEmptyString(details.tenantDomain) ?? '',
    baseUrl: asNonEmptyString(details.baseUrl) ?? '',
    spaceId: asNonEmptyString(details.spaceId) ?? '',
    pageId: asNonEmptyString(details.pageId) ?? '',
    locale: asNonEmptyString(details.locale) ?? 'en-US',
  };
}

export function parseHandshakeAck(
  msg: unknown,
): { type: typeof CONNECTION_EVENTS.handshakeAck; payload: HandshakeAckPayload } | null {
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.handshakeAck) return null;
  if (!m.payload || typeof m.payload !== 'object') return null;

  const partial = m.payload as Partial<HandshakeAckPayload>;
  const authToken = normalizeAuthToken(partial.authToken);
  const extensionDetails = normalizeExtensionDetails(partial.extensionDetails);
  if (!authToken || !extensionDetails) return null;

  return {
    type: CONNECTION_EVENTS.handshakeAck,
    payload: {
      authToken,
      extensionDetails,
      designTokens: normalizeDesignTokens(partial.designTokens),
    },
  };
}

export function parseTokenRefresh(
  msg: unknown,
): { type: typeof CONNECTION_EVENTS.tokenRefresh; payload: TokenRefreshPayload } | null {
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.tokenRefresh) return null;
  if (!m.payload || typeof m.payload !== 'object') return null;

  const authToken = normalizeAuthToken((m.payload as Partial<TokenRefreshPayload>).authToken);
  return authToken ? { type: CONNECTION_EVENTS.tokenRefresh, payload: { authToken } } : null;
}

/** Human-readable reasons a `HANDSHAKE_ACK` was refused, for the dev console. */
export function describeHandshakeAckFailure(msg: unknown): string[] {
  const reasons: string[] = [];
  if (!msg || typeof msg !== 'object') return ['message is not an object'];

  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.handshakeAck) {
    return [`type is ${String(m.type)} (expected ${CONNECTION_EVENTS.handshakeAck})`];
  }
  if (!m.payload || typeof m.payload !== 'object') return ['payload is missing'];

  const partial = m.payload as Partial<HandshakeAckPayload>;
  if (!asNonEmptyString((partial.authToken as { jwt?: unknown } | undefined)?.jwt)) {
    reasons.push('payload.authToken.jwt is missing or not a string');
  }
  if (!asNonEmptyString(partial.extensionDetails?.tenantId)) {
    reasons.push('payload.extensionDetails.tenantId is missing or not a string');
  }
  return reasons;
}
